import React, { useEffect, useState } from "react"
import { Row, Col, Badge, Container, Button, Stack, Form, Modal, Spinner } from "react-bootstrap"
import { useCompanies } from "../../api/companies/store"
import { useAuth } from "../../api/auth/store"
import { requestIpWhitelistOtp, addCompanyIpWhitelist, removeCompanyIpWhitelist } from "../../api/companies/actions"
import Loading from "../../components/Loading"
import CStack from "../../components/CStack"
import CAccordion from "../../components/CAccordion"
import ButtonControl from "../../components/ButtonControl"
import useUXStore from "../../api/ux/store"
import { ADMIN_ROLE, OWNER_ROLE } from "../../constants"
import SwitchControl from "../../components/SwitchControl"
import BaseControl from "../../components/BaseControl"

type WhitelistEntry = {
    ip: string
    addedAt: number
    addedBy: string
    description: string
}

const IpWhitelistItem = ({ entry, canEdit, onRemove, canRemove = true }: {
    entry: WhitelistEntry,
    canEdit: boolean,
    onRemove: (ip: string) => void,
    canRemove?: boolean
}) => {
    return (
        <CAccordion
            label={entry.ip}
        >
            <Container fluid className="py-3">
                <Row>
                    <Col md={6}>
                        <div className="mb-2"><strong>IP Address:</strong> {entry.ip}</div>
                        <div className="mb-2"><strong>Added By:</strong> {entry.addedBy}</div>
                        <div className="mb-2"><strong>Added At:</strong> {new Date(entry.addedAt * 1000).toLocaleString()}</div>
                    </Col>
                    <Col md={6}>
                        <div className="mb-2"><strong>Description:</strong> {entry.description || "No description"}</div>
                    </Col>
                </Row>
                {canEdit && (
                    <div className="mt-3">
                        {canRemove && <ButtonControl
                            text="Remove from Whitelist"
                            variant="outline-danger"
                            buttonLabel="Remove"
                            onClicked={() => onRemove(entry.ip)}
                            enabled={canRemove}
                        />}
                    </div>
                )}
            </Container>
        </CAccordion>
    )
}

const CompanyIpWhitelist = () => {
    const { profile, hasRole, retrieveCompanyProfile, sessionDetails, loadSession } = useAuth(state => state)
    const { toggleIpWhitelist, whitelist, loadingWhitelist, retrieveIpWhitelist } = useCompanies(state => state)
    const { addToast } = useUXStore(state => state)

    const [loading, setLoading] = useState(false)
    const [whitelistEnabled, setWhitelistEnabled] = useState(profile?.ip_whitelist || false)

    // Add/Remove Dialog State
    const [showModal, setShowModal] = useState(false)
    const [modalAction, setModalAction] = useState<"add" | "remove">("add")
    const [targetIp, setTargetIp] = useState("")
    const [description, setDescription] = useState("")
    const [otpCode, setOtpCode] = useState("")
    const [otpRequested, setOtpRequested] = useState(false)
    const [modalLoading, setModalLoading] = useState(false)

    useEffect(() => {
        if (!sessionDetails) {
            loadSession()
        }
    }, [sessionDetails])

    useEffect(() => {
        if (!profile) {
            setWhitelistEnabled(false)
            retrieveCompanyProfile()
        } else {
            setWhitelistEnabled(profile.ip_whitelist || false)
        }
    }, [profile])

    useEffect(() => {
        retrieveIpWhitelist()
    }, [])

    const handleToggle = async () => {
        const nextState = !whitelistEnabled
        setLoading(true)
        try {
            const code = await toggleIpWhitelist(nextState)
            if (code === "200") {
                setWhitelistEnabled(nextState)
                retrieveCompanyProfile()
                addToast("Success", `IP Whitelisting ${nextState ? "enabled" : "disabled"}`)
                // Refresh whitelist as enabling auto-adds current IP
                await retrieveIpWhitelist()
            } else {
                addToast("Error", "Failed to update IP whitelist setting", null, "danger")
            }
        } catch (err) {
            addToast("Error", "An error occurred", null, "danger")
        } finally {
            setLoading(false)
        }
    }

    const handleOpenModal = (action: "add" | "remove", ip: string = "") => {
        setModalAction(action)
        setTargetIp(ip)
        setDescription("")
        setOtpCode("")
        setOtpRequested(false)
        setShowModal(true)
    }

    const handleRequestOtp = async () => {
        setModalLoading(true)
        try {
            const code = await requestIpWhitelistOtp()
            if (code === "200" || code === "OTP sent") {
                setOtpRequested(true)
                addToast("Success", "Verification code sent to your email")
            } else {
                console.log(code)
                addToast("Error", "Failed to send verification code", null, "danger")
            }
        } catch (err) {
            console.log(err)
            addToast("Error", "An error occurred", null, "danger")
        } finally {
            setModalLoading(false)
        }
    }

    const handleConfirmAction = async () => {
        if (!otpRequested) {
            addToast("Warning", "Please request a verification code first", null, "warning")
            return
        }
        if (!otpCode) {
            addToast("Warning", "Please enter the verification code", null, "warning")
            return
        }

        setModalLoading(true)
        try {
            let code = ""
            if (modalAction === "add") {
                code = await addCompanyIpWhitelist(targetIp, description, otpCode)
            } else {
                code = await removeCompanyIpWhitelist(targetIp, otpCode)
            }

            if (code === "200") {
                addToast("Success", `IP address ${modalAction === "add" ? "added" : "removed"} successfully`)
                setShowModal(false)
                retrieveIpWhitelist()
            } else if (code === "otp_invalid") {
                addToast("Error", "Invalid or expired verification code", null, "danger")
            } else if (code === "limit_reached") {
                addToast("Error", "Maximum search of 20 whitelisted IPs reached", null, "danger")
            } else if (code === "already_whitelisted") {
                addToast("Error", "IP address is already whitelisted", null, "danger")
            } else {
                addToast("Error", `Failed to ${modalAction} IP address`, null, "danger")
            }
        } catch (err) {
            addToast("Error", "An error occurred", null, "danger")
        } finally {
            setModalLoading(false)
        }
    }

    if (!sessionDetails) {
        return <Loading center />
    }

    const canEdit = (hasRole(ADMIN_ROLE) || hasRole(OWNER_ROLE))

    return (
        <>
            <CStack header="">
                <SwitchControl
                    text="IP Whitelisting"
                    caption="Restrict access to your company account to specific IP addresses."
                    value={whitelistEnabled}
                    onChanged={handleToggle}
                    edit={!loading && canEdit}
                />
                {!whitelistEnabled && <div className="mt-2 p-3 bg-light border-start border-primary border-4 rounded-end small text-secondary">
                    <Stack direction="horizontal" gap={2} className="align-items-start">
                        <div className="text-primary mt-1">
                            <i className="bi bi-shield-lock"></i>
                        </div>
                        <div>
                            <strong>Extra Security:</strong> Enabling this ensures only authorized network locations can access your company data. It is a powerful defense against phishing and credential theft. When enabled, your current IP will be automatically added to prevent accidental lockout.
                        </div>
                    </Stack>
                </div>}
            </CStack>

            <CStack
                header={`Whitelisted IPs (${whitelist?.length || 0}/20)`}
                actions={canEdit && whitelistEnabled ? [
                    {
                        icon: <Button size="sm" variant="outline-primary">Add IP</Button>,
                        onClick: () => handleOpenModal("add"),
                        disabled: (whitelist?.length || 0) >= 20
                    }
                ] : []}
            >

                {loadingWhitelist ? <Loading /> : (
                    !whitelist || whitelist.length === 0 ? (
                        <div className="p-4 text-center text-muted border rounded bg-light">No whitelisted IP addresses</div>
                    ) : (
                        <div className="d-flex flex-column gap-2">
                            {whitelist.map(entry => (
                                <IpWhitelistItem
                                    key={entry.ip}
                                    entry={entry}
                                    canEdit={canEdit}
                                    onRemove={(ip) => handleOpenModal("remove", ip)}
                                    canRemove={!whitelistEnabled || (whitelist?.length || 0) > 1}
                                />
                            ))}
                        </div>
                    )
                )}
            </CStack>

            {/* Verification Modal */}
            <Modal show={showModal} onHide={() => !modalLoading && setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{modalAction === "add" ? "Add IP address" : "Remove IP address"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-4 small">
                        For security, this action requires email verification.
                    </p>
                    <Form>
                        {modalAction === "add" && (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label>IP Address</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. 192.168.1.1"
                                        value={targetIp}
                                        onChange={(e) => setTargetIp(e.target.value)}
                                        disabled={otpRequested}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Office location, Home, etc."
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        disabled={otpRequested}
                                    />
                                </Form.Group>
                            </>
                        )}
                        {modalAction === "remove" && (
                            <div className="mb-3">
                                <strong>Removing IP:</strong> <code className="ms-2">{targetIp}</code>
                            </div>
                        )}

                        <div className="d-flex align-items-end gap-2 mb-3">
                            <Form.Group className="flex-grow-1">
                                <Form.Label>Verification Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter 6-digit code"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value)}
                                    disabled={!otpRequested || modalLoading}
                                />
                            </Form.Group>
                            <Button
                                variant="outline-primary"
                                onClick={handleRequestOtp}
                                disabled={modalLoading || (modalAction === "add" && !targetIp)}
                            >
                                {otpRequested ? "Resend" : "Send Code"}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={modalLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant={modalAction === "add" ? "primary" : "danger"}
                        onClick={handleConfirmAction}
                        disabled={modalLoading || !otpCode}
                    >
                        {modalLoading ? <Spinner size="sm" /> : (modalAction === "add" ? "Add IP" : "Remove IP")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default CompanyIpWhitelist
